import React, { useEffect, useState } from 'react';
import {
  Trash2,
  Plus,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Scale
} from 'lucide-react';
import { OrderItem, Vendor } from '../types';
import { VendorSelector } from './VendorSelector';

// Common units for manual selection
const COMMON_UNITS = [
  { displayName: 'Kilogram', value: 'kg' },
  { displayName: 'Gram', value: 'g' },
  { displayName: 'Milligram', value: 'mg' },
  { displayName: 'Liter', value: 'L' },
  { displayName: 'Milliliter', value: 'ml' },
  { displayName: 'Gallon', value: 'gal' },
  { displayName: 'Ounce (fluid)', value: 'fl oz' },
  { displayName: 'Ounce (weight)', value: 'oz' },
  { displayName: 'Pound', value: 'lb' },
  { displayName: 'Pounds', value: 'lbs' },
  { displayName: 'Dozen', value: 'doz' },
  { displayName: 'Case', value: 'cs' },
  { displayName: 'Bushel', value: 'bu' },
  { displayName: 'Quart', value: 'qt' },
  { displayName: 'Pint', value: 'pt' },
  { displayName: 'Pieces', value: 'pcs' },
  { displayName: 'Pack', value: 'pk' },
  { displayName: 'Roll', value: 'rl' },
  { displayName: 'Box', value: 'box' },
  { displayName: 'Bags', value: 'bgs' },
  { displayName: 'Bunches', value: 'bnc' },
  { displayName: 'Bottles', value: 'btl' },
  { displayName: 'Heads', value: 'hds' },
  { displayName: 'Container', value: 'cnt' }
];

interface OrderItemsTableProps {
  items: OrderItem[];
  vendors: Vendor[];
  selectedUnits: Record<number, string>;
  itemVendorAssignments: Record<number, string>;
  onUpdateItem: (
    index: number,
    field: keyof OrderItem,
    value: string | number
  ) => void;
  onRemoveItem: (index: number) => void;
  onAddItem: () => void;
  onUpdateUnit: (index: number, unit: string) => void;
  onAssignVendor: (index: number, vendor: Vendor) => void;
  isSupplier?: boolean;
}

export const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  items,
  vendors,
  selectedUnits,
  itemVendorAssignments,
  onUpdateItem,
  onRemoveItem,
  onAddItem,
  onUpdateUnit,
  onAssignVendor,
  isSupplier = true
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState<number | null>(null);

  // Remove the void statement - we now use onAssignVendor

  // Auto-fill suggested_unit when item is first loaded
  useEffect(() => {
    items.forEach((item, index) => {
      if (item.suggested_unit && !selectedUnits[index] && !item.unit) {
        onUpdateUnit(index, item.suggested_unit);
      }
    });
  }, [items, onUpdateUnit]);

  const toggleRow = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const getEffectiveUnit = (item: OrderItem, index: number): string | null => {
    return selectedUnits[index] ?? item.unit ?? item.suggested_unit ?? null;
  };

  if (items.length === 0) {
    return (
      <div className="light flex-1 flex flex-col items-center justify-center text-gray-400 p-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Plus className="w-8 h-8 text-gray-300" />
        </div>
        <p className="text-lg font-medium text-gray-600">No items yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Parse an order or add items manually
        </p>
      </div>
    );
  }

  return (
    <div className="light flex-1 pr-6 min-h-[70vh] overflow-hidden flex flex-col text-gray-900">
      {/* Desktop Table Header */}
      <div className="hidden md:flex items-center gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="w-10"></div>
        <div className="flex-1 min-w-0">Item</div>
        <div className="w-24 text-center">Qty</div>
        <div className="w-32 text-center">Unit</div>
        {isSupplier && <div className="w-48">Vendor</div>}
        <div className="w-10"></div>
      </div>

      {/* Table Body */}
      <div className="flex-1 overflow-y-auto">
        {items.map((item, index) => {
          const effectiveUnit = getEffectiveUnit(item, index);
          const isNew = item.is_new;
          const isExpanded = expandedRows.has(index);
          const assignedVendorId =
            itemVendorAssignments[index] || item.vendor_id;
          const assignedVendor = isNew
            ? vendors.find(v => v.id === assignedVendorId) || null
            : item.vendor || null;
          const hasMultiple = !!item.suggested_units?.length;
          // Using void to satisfy TypeScript
          void assignedVendor;
          void hasMultiple;

          return (
            <div
              key={index}
              className={`border-b border-gray-100 ${isNew ? 'bg-amber-50/30' : ''}`}
            >
              {/* Mobile Card View */}
              <div className="md:hidden p-4" onClick={() => toggleRow(index)}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* <div className="flex items-center gap-2 mb-2">
                                            {isNew && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                                    <Sparkles className="w-3 h-3" />
                                                    New
                                                </span>
                                            )}
                                            {!isNew && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    Exists
                                                </span>
                                            )}
                                        </div> */}
                    <input
                      type="text"
                      value={item.item}
                      onChange={e =>
                        onUpdateItem(index, 'item', e.target.value)
                      }
                      onClick={e => e.stopPropagation()}
                      className={`w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-base font-medium text-gray-900 placeholder-gray-400 ${isNew ? 'text-amber-900' : ''}`}
                      placeholder="Item name"
                    />
                    {item.warning && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {item.warning}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveItem(index);
                    }}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove item"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={e =>
                        onUpdateItem(
                          index,
                          'quantity',
                          parseInt(e.target.value) || 1
                        )
                      }
                      onClick={e => e.stopPropagation()}
                    className={`w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-base font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 ${item.warning ? 'border-red-300 bg-red-50 text-red-900' : ''}`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      Unit
                    </label>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setUnitDropdownOpen(
                          unitDropdownOpen === index ? null : index
                        );
                      }}
                      className="flex items-center justify-between w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-base hover:border-indigo-300 transition-colors"
                    >
                      <span className="text-gray-700">
                        {effectiveUnit || 'Select'}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 transition-transform ${unitDropdownOpen === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>
                </div>

                {unitDropdownOpen === index && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUnitDropdownOpen(null)}
                    />
                    <div className="absolute z-20 left-4 right-4 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                      <div className="max-h-60 overflow-y-auto">
                        {item.suggested_units &&
                        item.suggested_units.length > 0 ? (
                          <>
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                              <p className="text-xs font-medium text-gray-500">
                                Suggested Units
                              </p>
                            </div>
                            {item.suggested_units.map((unit, i) => (
                              <button
                                key={i}
                                onClick={e => {
                                  e.stopPropagation();
                                  onUpdateUnit(index, unit.unit);
                                  setUnitDropdownOpen(null);
                                }}
                                className={`w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center justify-between ${
                                  effectiveUnit === unit.unit
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'text-gray-700'
                                }`}
                              >
                                <span>{unit.unit}</span>
                                {unit.isPrimary && (
                                  <span className="text-xs text-gray-400">
                                    common
                                  </span>
                                )}
                              </button>
                            ))}
                          </>
                        ) : null}

                        <div
                          className={`${item.suggested_units && item.suggested_units.length > 0 ? 'border-t border-gray-100' : ''}`}
                        >
                          <div className="px-3 py-2 bg-gray-50">
                            <p className="text-xs font-medium text-gray-500">
                              Common Units
                            </p>
                          </div>
                          {COMMON_UNITS.map((unit, i) => (
                            <button
                              key={i}
                              onClick={e => {
                                e.stopPropagation();
                                onUpdateUnit(index, unit.value);
                                setUnitDropdownOpen(null);
                              }}
                              className={`w-full px-4 py-3 text-left text-base hover:bg-gray-50 flex items-center justify-between ${
                                effectiveUnit === unit.value
                                  ? 'bg-indigo-50 text-indigo-700'
                                  : 'text-gray-700'
                              }`}
                            >
                              <span>{unit.displayName}</span>
                              <span className="text-xs text-gray-400">
                                ({unit.value})
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Note input for mobile */}
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Note (optional)
                  </label>
                  <input
                    type="text"
                    value={item.note || ''}
                    onChange={e => onUpdateItem(index, 'note', e.target.value)}
                    onClick={e => e.stopPropagation()}
                    placeholder="Add note..."
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-base text-gray-900 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Desktop Row View */}
              <div
                className={`hidden md:flex items-center gap-4 p-4 cursor-pointer transition-colors ${
                  isNew
                    ? 'bg-amber-50/30 hover:bg-amber-50'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => toggleRow(index)}
                onMouseEnter={() => setHoveredRow(index)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {/* Item Name */}
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={item.item}
                    onChange={e => onUpdateItem(index, 'item', e.target.value)}
                    onClick={e => e.stopPropagation()}
                    className={`w-full bg-transparent border-none focus:ring-0 p-0 text-sm font-medium text-gray-900 placeholder-gray-400 ${
                      isNew ? 'text-amber-900' : ''
                    }`}
                    placeholder="Item name"
                  />
                  {item.warning && (
                    <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {item.warning}
                    </p>
                  )}
                </div>

                {/* Quantity */}
                <div className="w-24 text-center">
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={e =>
                      onUpdateItem(
                        index,
                        'quantity',
                        parseInt(e.target.value) || 1
                      )
                    }
                    onClick={e => e.stopPropagation()}
                    className={`w-16 text-center bg-transparent border border-gray-200 rounded px-2 py-1 text-sm font-medium text-gray-900 focus:ring-2 focus:ring-indigo-500 ${
                      item.warning
                        ? 'border-red-300 bg-red-50 text-red-900'
                        : ''
                    }`}
                  />
                </div>

                {/* Unit with Dropdown */}
                <div className="w-32 text-center relative">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setUnitDropdownOpen(
                        unitDropdownOpen === index ? null : index
                      );
                    }}
                    className="flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm hover:border-indigo-300 transition-colors"
                  >
                    <span className="text-gray-700">
                      {effectiveUnit || 'Select'}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${unitDropdownOpen === index ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {unitDropdownOpen === index && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setUnitDropdownOpen(null)}
                      />
                      <div className="absolute z-20 top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                        <div className="max-h-60 overflow-y-auto">
                          {item.suggested_units &&
                          item.suggested_units.length > 0 ? (
                            <>
                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                <p className="text-xs font-medium text-gray-500">
                                  Suggested Units
                                </p>
                              </div>
                              {item.suggested_units.map((unit, i) => (
                                <button
                                  key={i}
                                  onClick={e => {
                                    e.stopPropagation();
                                    onUpdateUnit(index, unit.unit);
                                    setUnitDropdownOpen(null);
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                    effectiveUnit === unit.unit
                                      ? 'bg-indigo-50 text-indigo-700'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  <span>{unit.unit}</span>
                                  {unit.isPrimary && (
                                    <span className="text-xs text-gray-400">
                                      common
                                    </span>
                                  )}
                                </button>
                              ))}
                            </>
                          ) : null}

                          <div
                            className={`${item.suggested_units && item.suggested_units.length > 0 ? 'border-t border-gray-100' : ''}`}
                          >
                            <div className="px-3 py-2 bg-gray-50">
                              <p className="text-xs font-medium text-gray-500">
                                Common Units
                              </p>
                            </div>
                            {COMMON_UNITS.map((unit, i) => (
                              <button
                                key={i}
                                onClick={e => {
                                  e.stopPropagation();
                                  onUpdateUnit(index, unit.value);
                                  setUnitDropdownOpen(null);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                                  effectiveUnit === unit.value
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'text-gray-700'
                                }`}
                              >
                                <span>{unit.displayName}</span>
                                <span className="text-xs text-gray-400">
                                  ({unit.value})
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Vendor Selector - Only show for suppliers */}
                {isSupplier && (
                  <div className="w-48">
                    <VendorSelector
                      vendors={vendors}
                      selectedVendorId={assignedVendorId || null}
                      assignedVendor={assignedVendor}
                      onSelectVendor={vendor => onAssignVendor(index, vendor)}
                      isNew={!!isNew}
                    />
                  </div>
                )}

                {/* Expand/Collapse & Actions */}
                <div className="flex items-center gap-2 w-10">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      toggleRow(index);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveItem(index);
                    }}
                    className={`p-1 rounded transition-colors ${
                      hoveredRow === index
                        ? 'text-red-500 hover:bg-red-50'
                        : 'text-gray-300 hover:text-red-500'
                    }`}
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Details - Desktop Only */}
              {isExpanded && (
                <div className="hidden md:block bg-gray-50 border-t border-gray-100 p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {/* Note */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Note
                      </label>
                      <input
                        type="text"
                        value={item.note || ''}
                        onChange={e =>
                          onUpdateItem(index, 'note', e.target.value)
                        }
                        placeholder="Add note..."
                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    {/* Suggested Unit */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                        <Scale className="w-3 h-3" />
                        Suggested Unit
                      </label>
                      <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                        {item.suggested_unit || 'None'}
                      </div>
                    </div>

                    {/* Warning */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Warning
                      </label>
                      <div
                        className={`px-3 py-2 rounded-lg text-sm ${
                          item.warning
                            ? 'bg-red-50 border border-red-200 text-red-700'
                            : 'bg-white border border-gray-200 text-gray-400'
                        }`}
                      >
                        {item.warning || 'No issues'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Item Button */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onAddItem}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-base font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add New Item
        </button>
      </div>
    </div>
  );
};
